import React, { CSSProperties, useRef } from "react";
import { HsvColor } from "@common/helpers/color";
import styles from "./BackgroundColorPicker.module.scss";

interface Props {
    hsv: HsvColor;
    onChange: (hsv: HsvColor) => void;
}

const KEYBOARD_STEP = 2;

function toPercent({ position, size }: { position: number; size: number }) {
    if (size <= 0) {
        return 0;
    }
    return Math.min(100, Math.max(0, (position / size) * 100));
}

/**
 * Saturation (x) and brightness (y) plane, the same gesture the browser's own
 * picker offers. ARIA has no two-axis slider, so the plane exposes its position
 * as text and moves with the arrow keys; the hex field next to it stays the
 * exact-entry path.
 */
export const ColorSaturationArea: React.FC<Props> = ({ hsv, onChange }) => {
    const areaRef = useRef<HTMLDivElement>(null);

    const applyPointer = (event: React.PointerEvent<HTMLDivElement>) => {
        const area = areaRef.current;
        if (!area) {
            return;
        }
        const bounds = area.getBoundingClientRect();
        onChange({
            ...hsv,
            saturation: toPercent({
                position: event.clientX - bounds.left,
                size: bounds.width,
            }),
            value:
                100 -
                toPercent({
                    position: event.clientY - bounds.top,
                    size: bounds.height,
                }),
        });
    };

    const areaStyle: CSSProperties & { "--picker-hue": number } = {
        "--picker-hue": hsv.hue,
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const step = event.shiftKey ? KEYBOARD_STEP * 5 : KEYBOARD_STEP;
        const moves: Record<string, Partial<HsvColor>> = {
            ArrowLeft: { saturation: hsv.saturation - step },
            ArrowRight: { saturation: hsv.saturation + step },
            ArrowUp: { value: hsv.value + step },
            ArrowDown: { value: hsv.value - step },
        };
        const move = moves[event.key];
        if (!move) {
            return;
        }
        event.preventDefault();
        const next = { ...hsv, ...move };
        onChange({
            ...next,
            saturation: Math.min(100, Math.max(0, next.saturation)),
            value: Math.min(100, Math.max(0, next.value)),
        });
    };

    return (
        <div
            ref={areaRef}
            role="slider"
            tabIndex={0}
            aria-label="Saturation and brightness"
            aria-valuetext={`${Math.round(hsv.saturation)}% saturation, ${Math.round(
                hsv.value,
            )}% brightness`}
            className={styles.saturationArea}
            style={areaStyle}
            onPointerDown={(event) => {
                event.currentTarget.setPointerCapture(event.pointerId);
                applyPointer(event);
            }}
            onPointerMove={(event) => {
                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                    applyPointer(event);
                }
            }}
            onKeyDown={handleKeyDown}
        >
            <span
                className={styles.saturationHandle}
                style={{
                    left: `${hsv.saturation}%`,
                    top: `${100 - hsv.value}%`,
                }}
            />
        </div>
    );
};
